import { getMultiAgentAuthHeaders, multiAgentBaseUrl } from "./client";
import { registerRunTicket } from "./sse";
import type { ArtifactContent, RunCreatedOut, RunDetail, RunHistoryItem } from "./types";

const BASE = multiAgentBaseUrl;

export type MultiAgentAttachmentKind = "image" | "pdf" | "audio" | "url" | "text" | "code";

/** A multimodal intake attachment. `markdown` is filled after /preprocess. */
export interface MultiAgentAttachment {
  id: string;
  kind: MultiAgentAttachmentKind;
  name: string;
  status: "processing" | "ready" | "error";
  mime?: string;
  language?: string;
  content?: string;        // text/code body or URL string
  data_base64?: string;    // binary (image/pdf/audio)
  markdown?: string;       // resolved by /preprocess
  thumbnailDataUrl?: string; // images only (client preview)
  errorReason?: string;    // why resolution failed (status === "error")
  // URL chips — page title + favicon resolved by /preprocess (optional; the
  // backend fills these for `url` kind, falls back to the raw URL when absent).
  title?: string;
  favicon?: string;
  // Image/audio/pdf — the backend signals when Gemini extraction was cut short
  // (finish_reason !== STOP / clipped to MAX_INPUT_CHARS) so the chip can warn
  // the user the folded text "may be incomplete". Optional → back-compatible.
  truncated?: boolean;
}

/** Resolve ONE attachment (image/pdf/audio/url/text/code) → markdown via the
 *  backend pre-processor (Gemini for binary, fetch for URL, passthrough else). */
export async function preprocessAttachment(input: {
  kind: MultiAgentAttachmentKind;
  name: string;
  mime?: string;
  language?: string;
  content?: string;
  data_base64?: string;
}): Promise<{
  kind: string;
  name: string;
  markdown: string;
  ok?: boolean;
  error?: string | null;
  // OPTIONAL (backward-compatible) — the backend adds these progressively:
  //   url:           title + favicon of the fetched page
  //   image/pdf/audio: truncated / finish_reason when extraction was cut short
  title?: string | null;
  favicon?: string | null;
  truncated?: boolean | null;
  finish_reason?: string | null;
}> {
  const res = await fetch(`${BASE}/api/runs/preprocess`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getMultiAgentAuthHeaders() },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`preprocess ${res.status}: ${await res.text()}`);
  return res.json();
}

export async function createRun(
  user_intent: string,
  session_id?: string | null,
  attachments?: MultiAgentAttachment[] | null,
): Promise<RunCreatedOut> {
  // Only the resolved markdown + meta ride to the run (keeps the body small).
  const atts = (attachments || [])
    .filter((a) => a.markdown && a.markdown.trim())
    .map((a) => ({ kind: a.kind, name: a.name, markdown: a.markdown, language: a.language }));
  const res = await fetch(`${BASE}/api/runs`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getMultiAgentAuthHeaders(),
    },
    body: JSON.stringify({
      user_intent,
      ...(session_id ? { session_id } : {}),
      ...(atts.length ? { attachments: atts } : {}),
    }),
  });
  if (!res.ok) throw new Error(`create_run ${res.status}: ${await res.text()}`);
  const out: RunCreatedOut = await res.json();
  registerRunTicket(out.run_id, out.sse_ticket);
  return out;
}

/** Blocked-state retrigger (dev-agent parity): kicks off RAG ingest when the
 *  org's code index is not_started/failed. `endpoint` comes from the run's
 *  blocked_action (default `/api/rag/trigger-ingest`). Throws with the backend's
 *  detail.message on a 409/502/503 so the caller can toast it. */
export async function triggerRagIngest(
  endpoint = "/api/rag/trigger-ingest",
): Promise<Record<string, unknown>> {
  const res = await fetch(`${BASE}${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getMultiAgentAuthHeaders() },
  });
  if (!res.ok) {
    let message = `trigger-ingest ${res.status}`;
    try {
      const detail = (await res.json())?.detail;
      if (detail?.message) message = detail.message;
    } catch { /* keep default */ }
    throw new Error(message);
  }
  return res.json();
}

export interface MultiAgentDocResult {
  title: string;
  url: string;
  snippet: string;
  category?: string;
  source?: string;
}

/** Self-contained docs search (multi-step-agentic's own Exa endpoint —
 *  NOT agentic-platform). Same shape as the old docsService. */
export async function searchMultiAgentDocs(
  query: string,
  limit = 8,
  opts?: { sessionId?: string | null; runId?: string | null },
): Promise<{ answer?: string; results: MultiAgentDocResult[]; query: string; cached: boolean; session_id?: string | null }> {
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  // Thread the session + a client-owned turn id so the backend records this
  // docs Q&A as a session turn (visible to follow-ups + persisted across reload).
  if (opts?.sessionId) params.set("session_id", opts.sessionId);
  if (opts?.runId) params.set("run_id", opts.runId);
  const res = await fetch(
    `${BASE}/api/docs/search?${params.toString()}`,
    { method: "GET", headers: { ...getMultiAgentAuthHeaders() } },
  );
  if (!res.ok) throw new Error(`docs_search ${res.status}`);
  return res.json();
}

/** Delete an entire session (all its runs), scoped to the caller's org+user. */
export async function deleteMultiAgentSession(
  sessionId: string,
): Promise<{ deleted: boolean; session_id: string; run_count: number }> {
  const res = await fetch(`${BASE}/api/runs/session/${encodeURIComponent(sessionId)}`, {
    method: "DELETE",
    headers: { ...getMultiAgentAuthHeaders() },
  });
  if (!res.ok) throw new Error(`delete_session ${res.status}: ${await res.text()}`);
  return res.json();
}

export async function listRecentRuns(): Promise<{ runs: RunHistoryItem[] }> {
  const res = await fetch(`${BASE}/api/runs`, {
    method: "GET",
    headers: { ...getMultiAgentAuthHeaders() },
  });
  if (!res.ok) throw new Error(`list_runs ${res.status}`);
  return res.json();
}

export async function getArtifact(artifactId: string): Promise<ArtifactContent> {
  const res = await fetch(`${BASE}/api/artifacts/${encodeURIComponent(artifactId)}`, {
    method: "GET",
    headers: { ...getMultiAgentAuthHeaders() },
  });
  if (!res.ok) throw new Error(`artifact ${res.status}`);
  return res.json();
}

export interface ApexValidateResult {
  valid: boolean;
  saved: boolean;
  errors: { message: string; line?: number }[];
  attempts: number;
  validation_status?: string;
  content?: string;
}

/** On-demand CHECK-ONLY compile + auto-fix for an emitted Apex artifact. Never
 *  deploys or persists — returns validity, any remaining errors, and the (possibly
 *  auto-fixed) source. Backs the "Validate" button on code artifact cards. */
export async function validateApex(
  runId: string,
  className: string,
  content: string,
  metadataType = "ApexClass",
): Promise<ApexValidateResult> {
  const res = await fetch(`${BASE}/api/runs/${encodeURIComponent(runId)}/validate-save`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getMultiAgentAuthHeaders() },
    body: JSON.stringify({ class_name: className, content, metadata_type: metadataType }),
  });
  if (!res.ok) throw new Error(`validate ${res.status}`);
  return res.json();
}

export async function getRunDetail(runId: string): Promise<RunDetail> {
  const res = await fetch(`${BASE}/api/runs/${encodeURIComponent(runId)}/detail`, {
    method: "GET",
    headers: { ...getMultiAgentAuthHeaders() },
  });
  if (!res.ok) throw new Error(`run_detail ${res.status}`);
  return res.json();
}

export async function refineRun(
  runId: string,
  question: string,
  scope?: Record<string, unknown>,
): Promise<RunCreatedOut & { parent_run_id?: string }> {
  const res = await fetch(`${BASE}/api/runs/${encodeURIComponent(runId)}/refine`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getMultiAgentAuthHeaders() },
    body: JSON.stringify({ question, ...(scope ? { scope } : {}) }),
  });
  if (!res.ok) throw new Error(`refine ${res.status}: ${await res.text()}`);
  const out: RunCreatedOut & { parent_run_id?: string } = await res.json();
  registerRunTicket(out.run_id, out.sse_ticket);
  return out;
}

/** Coverage entry shape shared by the run-test response and the
 *  `test_run_completed` SSE event. Mirrors the dev-agent `coveredClasses`
 *  shape exactly so the multi-agent IDE panels can reuse dev-agent rendering. */
export interface MultiAgentCoveredClass {
  name: string;
  coverage_pct: number | null;
  covered_lines: number[];
  uncovered_lines: number[];
  num_covered: number;
  num_uncovered: number;
}

export interface MultiAgentRunTestResult {
  className: string;
  status: string;
  coveragePct: number | null;
  coveredClasses: MultiAgentCoveredClass[];
}

/**
 * Run an Apex test class against the org in the context of a multi-agent run.
 * Mirrors dev-agent's `runApexTest`, but targets the multi-agent backend:
 *   POST /api/runs/{run_id}/run-test  { test_class } -> MultiAgentRunTestResult
 * The backend ALSO emits a `test_run_completed` SSE event with the same body,
 * so agent-driven and user-driven runs converge on the store the same way.
 */
/** Hard client-side bound on a run-test round-trip. Slightly above the MSA
 *  backend's own 180s SFDX timeout so the server's cleaner 504 wins when it
 *  can, but a wedged connection can never hang the UI unboundedly. */
const RUN_TEST_TIMEOUT_MS = 200_000;

export async function runMultiAgentTest(
  runId: string,
  params: { test_class: string },
): Promise<MultiAgentRunTestResult> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), RUN_TEST_TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(`${BASE}/api/runs/${encodeURIComponent(runId)}/run-test`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getMultiAgentAuthHeaders() },
      body: JSON.stringify(params),
      signal: ctrl.signal,
    });
  } catch (err) {
    // Distinguish OUR timeout abort from a network failure — the store keys
    // its "timed out" toast off this message.
    if (ctrl.signal.aborted) {
      throw new Error(`run-test timed out after ${RUN_TEST_TIMEOUT_MS / 1000}s`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
  if (!res.ok) throw new Error(`run-test ${res.status}: ${await res.text()}`);
  return res.json();
}

/** Impact-analysis block returned by the post-save analyze endpoint. Mirrors
 *  the dev-agent `ImpactAnalysisResult` so the copied panels render unchanged. */
export interface MultiAgentImpactResult {
  blast_radius: number;
  callers: Array<{ name: string; type: string; risk: "high" | "medium" | "low" }>;
  callees: Array<{ name: string; type: string }>;
  data_access: Array<{ object: string; operations: string[] }>;
  triggers_at_risk: Array<{ trigger: string; object: string }>;
  references: Array<{ file: string; line: number; kind: string }>;
  summary: string;
}

/** Aggregate-coverage block returned by the post-save analyze endpoint. */
export interface MultiAgentCoverageResult {
  test_classes: Array<{
    name: string;
    coverage_pct: number | null;
    lines_covered: number | null;
    lines_uncovered: number | null;
  }>;
  aggregate_coverage_pct: number | null;
  summary: string;
}

/** Dependency block returned by the post-save analyze endpoint. */
export interface MultiAgentAnalyzeDependencies {
  target: string;
  target_id?: string | null;
  callers: Array<{ name: string; id?: string; metadata_type?: string }>;
  tests: Array<{ name: string; id?: string; metadata_type?: string }>;
  total?: number;
  captured_at?: string;
}

/** Response shape of POST /api/runs/{run_id}/analyze. Mirrors the dev-agent
 *  `PostSaveAnalysisResult`. Results ALSO arrive via the `impact_analysis` and
 *  `test_coverage` SSE events; this synchronous body is a best-effort echo. */
export interface MultiAgentPostSaveAnalysisResult {
  impact: MultiAgentImpactResult | null;
  coverage: MultiAgentCoverageResult | null;
  dependencies?: MultiAgentAnalyzeDependencies | null;
  class_name: string;
  metadata_type: string;
}

/**
 * Post-save analysis for the multi-agent IDE. Mirrors dev-agent's
 * `runPostSaveAnalysis` (POST /api/chat/sessions/{id}/analyze), but targets the
 * multi-agent backend:
 *   POST /api/runs/{run_id}/analyze  { class_name, metadata_type }
 * The backend ALSO emits `impact_analysis` / `test_coverage` / `dependencies_ready`
 * SSE events with the same payloads, so the IDE store is populated by the stream
 * (MA-7). The returned body is accepted but the SSE is the source of truth.
 */
export async function runMultiAgentPostSaveAnalysis(
  runId: string,
  params: { class_name: string; metadata_type: string },
): Promise<MultiAgentPostSaveAnalysisResult> {
  const res = await fetch(`${BASE}/api/runs/${encodeURIComponent(runId)}/analyze`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getMultiAgentAuthHeaders() },
    body: JSON.stringify(params),
  });
  if (!res.ok) throw new Error(`analyze ${res.status}: ${await res.text()}`);
  return res.json();
}

/** Compile-validation result from the multi-agent validate-save endpoint.
 *  Mirrors the dev-agent `validateAndSave` response shape. */
export interface MultiAgentValidateSaveResult {
  valid: boolean;
  saved: boolean;
  attempts: number;
  errors?: Array<{ message: string; line?: number }>;
  dependent_classes?: string[];
  /** "valid" | "unvalidated". "unvalidated" = NO validator could compile-check the
   *  code (e.g. a NEW class the Tooling API can't compile because it doesn't exist
   *  in the org yet, while the overlay validator was unavailable). This is NOT a
   *  compile error — the auto-fix loop can't help — so the IDE persists the code
   *  with a non-blocking warning instead of blocking the save. */
  validation_status?: string;
  /** Human-readable reason for an "unvalidated" result. */
  reason?: string;
  /** The validated content. When the backend's internal fix loop repaired the
   *  code, this is the FIXED source — the IDE must persist THIS, not the original
   *  (otherwise a non-compiling file ships to the branch). */
  content?: string;
}

/**
 * Compile-gate a class against the org for the multi-agent run (MA-11/MA-13).
 * Mirrors dev-agent's `validateAndSave` (POST /sessions/{id}/validate-save), but
 * targets the multi-agent backend:
 *   POST /api/runs/{run_id}/validate-save  { class_name, content, metadata_type }
 * This is the AUTHORITATIVE compile gate (MA-13) — the IDE save path calls it
 * BEFORE persisting a branch; on `valid:false` it feeds the auto-fix loop.
 */
export async function multiAgentValidateSave(
  runId: string,
  params: { class_name: string; content: string; metadata_type?: string },
  signal?: AbortSignal,
): Promise<MultiAgentValidateSaveResult> {
  const res = await fetch(`${BASE}/api/runs/${encodeURIComponent(runId)}/validate-save`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getMultiAgentAuthHeaders() },
    body: JSON.stringify(params),
    signal,
  });
  if (!res.ok) throw new Error(`validate-save ${res.status}: ${await res.text()}`);
  return res.json();
}

/** Auto-fix result from the multi-agent fix-code endpoint. Mirrors dev-agent. */
export interface MultiAgentFixCodeResult {
  fixed: boolean;
  content: string;
  errors_addressed?: string[];
}

/**
 * Ask the multi-agent backend to auto-fix compilation errors (MA-12).
 * Mirrors dev-agent's `fixCode` (POST /sessions/{id}/fix-code):
 *   POST /api/runs/{run_id}/fix-code  { class_name, content, errors, metadata_type }
 */
export async function multiAgentFixCode(
  runId: string,
  params: {
    class_name: string;
    content: string;
    errors: Array<{ message: string }>;
    metadata_type?: string;
  },
  signal?: AbortSignal,
): Promise<MultiAgentFixCodeResult> {
  const res = await fetch(`${BASE}/api/runs/${encodeURIComponent(runId)}/fix-code`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getMultiAgentAuthHeaders() },
    body: JSON.stringify(params),
    signal,
  });
  if (!res.ok) throw new Error(`fix-code ${res.status}: ${await res.text()}`);
  return res.json();
}

/** Launch a server-side `sf lightning dev` preview for a generated LWC and get
 *  back the org preview URL to open in a new tab. Credentials reuse the chat's
 *  org session (backend resolves them); the body is just the component bundle.
 *  Returns { preview_id, preview_url, instance_url }. */
export async function startLwcPreview(input: {
  component_name: string;
  files: Record<string, string>;
}): Promise<{ preview_id: string; preview_url: string; instance_url: string }> {
  const res = await fetch(`${BASE}/api/lwc-preview`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getMultiAgentAuthHeaders() },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`lwc-preview ${res.status}: ${await res.text()}`);
  return res.json();
}

/** Deliberately enable Live Preview (Local Dev) for a specific connected org —
 *  deploys the one `enableLightningPreviewPref` setting. Idempotent. Scopes the
 *  call to `orgId` (overrides the active-org header) so the Connection Manager's
 *  per-card toggle targets the right org. Needs admin / Modify Metadata perms. */
export async function enableLwcPreview(
  orgId?: string,
): Promise<{ enabled: boolean; detail: string }> {
  const headers: Record<string, string> = { ...getMultiAgentAuthHeaders() };
  if (orgId) headers["X-Organization-ID"] = orgId;
  const res = await fetch(`${BASE}/api/lwc-preview/enable`, { method: "POST", headers });
  if (!res.ok) throw new Error(`lwc-preview/enable ${res.status}: ${await res.text()}`);
  return res.json();
}

export async function sendFeedback(
  runId: string,
  rating: number,
  comment: string | null,
  reasons: string[] = [],
): Promise<void> {
  const res = await fetch(`${BASE}/api/runs/${encodeURIComponent(runId)}/feedback`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getMultiAgentAuthHeaders() },
    body: JSON.stringify({
      rating,
      ...(comment ? { comment } : {}),
      ...(reasons.length > 0 ? { reasons } : {}),
    }),
  });
  if (!res.ok) throw new Error(`feedback ${res.status}`);
}

export async function approveRun(runId: string): Promise<void> {
  const res = await fetch(`${BASE}/api/runs/${encodeURIComponent(runId)}/approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getMultiAgentAuthHeaders() },
    body: JSON.stringify({ approved: true }),
  });
  if (!res.ok && res.status !== 404) {
    throw new Error(`approve ${res.status}: ${await res.text()}`);
  }
}

export async function cancelRun(runId: string): Promise<void> {
  const res = await fetch(`${BASE}/api/runs/${encodeURIComponent(runId)}/cancel`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getMultiAgentAuthHeaders() },
  });
  if (!res.ok && res.status !== 404) {
    throw new Error(`cancel ${res.status}: ${await res.text()}`);
  }
}
